import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { toast } from 'sonner';

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

import { getMerchantDetails, loginAuthantication } from '../api';
import { localStorageItems, setMerchatApiKey, setMerchantDetails } from '../utility/helper';
import { saveAuth } from '../state/slices/authSlice';
import { saveMerchant } from '../state/slices/merchantSlice';
import { saveMerchantDetails, setLoading } from '../state/slices/testmodeSlice';
import { ACCESS_TOKEN } from '../api/constants';

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .email('Please enter a valid email address')
    .required('Please enter your email address'),
  password: Yup.string().required('Please enter your password'),
});

export function LoginForm({
  className,
  ...props
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // Check if already logged in
    const accessToken = localStorage.getItem(ACCESS_TOKEN);
    if (accessToken) {
      navigate('/home');
    }
  }, [navigate]);

  const getMerchantData = async (id) => {
    dispatch(setLoading(true));
    const response = await getMerchantDetails(id);
    dispatch(setLoading(false));
    if (response?.data?.code === 200) {
      setMerchatApiKey(response.data.data.account_details.api_key);
      setMerchantDetails(JSON.stringify(response.data.data.account_manager));
      dispatch(saveMerchant(response?.data?.data));
      dispatch(
        saveMerchantDetails(
          response?.data?.data?.account_details?.account_status
        )
      );
    } else if (response?.data?.code === 403) {
      dispatch(saveMerchantDetails('INREVIEW'));
    }
  };

  const handleLogin = async (values) => {
    setIsSubmitting(true);
    try {
      const loginValues = {
        ...values,
        email: String(values.email).toLowerCase(),
      };
      
      const response = await loginAuthantication(loginValues);
      if (response.status === 200) {
        localStorageItems(response);
        dispatch(saveAuth(response?.data?.data));
        await getMerchantData(response?.data?.data?.user?.merchant_user_id);
        navigate('/home');
      } else {
        toast.error('Email or password is incorrect. Please try again');
      }
    } catch (error) {
      toast.error('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <Card className="overflow-hidden">
        <CardContent className="grid p-0 md:grid-cols-2">
          <Formik
            initialValues={{ email: '', password: '' }}
            validationSchema={validationSchema}
            onSubmit={handleLogin}
          >
            {({ errors, touched, handleChange, handleBlur, handleSubmit, values, isValid, dirty }) => (
              <Form className="p-6 md:p-8" onSubmit={handleSubmit}>
                <div className="flex flex-col gap-6">
                  <div className="flex flex-col items-center text-center">
                    <h1 className="text-2xl font-bold">Welcome back</h1>
                    <p className="text-balance text-muted-foreground">
                      Login to your FAM account
                    </p>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="email">Email</Label>
                    <Input 
                      id="email" 
                      name="email"
                      type="email" 
                      placeholder="m@example.com" 
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={errors.email && touched.email ? "border-destructive" : ""}
                      required 
                    />
                    {errors.email && touched.email && (
                      <p className="text-sm text-destructive">{errors.email}</p>
                    )}
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="password">Password</Label>
                    <Input 
                      id="password" 
                      name="password"
                      type="password" 
                      value={values.password}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={errors.password && touched.password ? "border-destructive" : ""}
                      required 
                    />
                    {errors.password && touched.password && (
                      <p className="text-sm text-destructive">{errors.password}</p>
                    )}
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full"
                    disabled={!(isValid && dirty) || isSubmitting}
                  >
                    {isSubmitting ? 'Logging in...' : 'Login'}
                  </Button>
                  <div className="text-center text-sm">
                    Don&apos;t have an account?{" "}
                    <a 
                      href="https://calendly.com/urveshvasani" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="underline underline-offset-4"
                    >
                      Contact Sales
                    </a>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
          <div className="relative hidden bg-muted md:block">
            <img
              src="/images/login-hero.jpg"
              alt="Login hero image"
              className="absolute inset-0 h-full w-full object-cover dark:brightness-[0.2] dark:grayscale" />
          </div>
        </CardContent>
      </Card>
      <div
        className="text-balance text-center text-xs text-muted-foreground [&_a]:underline [&_a]:underline-offset-4 hover:[&_a]:text-primary">
        By clicking continue, you agree to our{" "}
        <a href="https://joinfam.com/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>{" "}
        and{" "}
        <a href="https://joinfam.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
      </div>
    </div>
  );
}
